import { useState, useEffect, useMemo } from "react";
import { fetchActiveProductKnowledgeJobs, downloadProductKnowledgeData } from "../../apis/productKnowledge";
import { Brands, BrandsFN } from "../../constants/enum";
import { ProductLibProcessJob } from "../../API";
import FolderView, { Product } from "./FolderView";
import TreeView from "./TreeView";
import { ExportPDFButton } from "./components";

// View types
type ViewType = "folder" | "tree";

// Brand logo mapping
const brandLogos: Record<string, string> = {
  FL: "/brands/fineline.png",
  DN: "/brands/deenee.png",
  BE: "/brands/benice.png",
  TS: "/brands/tros.png",
  SM: "/brands/smart.png",
  ES: "/brands/eversense.png",
  TM: "/brands/tomi.png",
  VV: "/brands/vivite.png",
  LT: "/brands/lovlitails.png",
};

// Brand theme colors
const brandThemeColors: Record<string, { bg: string; border: string; text: string }> = {
  FL: { bg: "#FFEEC8", border: "#F5C518", text: "#8B6914" },
  DN: { bg: "#C9DDFF", border: "#5B8DEF", text: "#2E5AAD" },
  BE: { bg: "#FFD0E8", border: "#E6006E", text: "#9C004A" },
  TS: { bg: "#D4D7E3", border: "#4A5278", text: "#2D3348" },
  SM: { bg: "#DCDEE1", border: "#9CA3AF", text: "#4B5563" },
  ES: { bg: "#FFD0E8", border: "#FF69B4", text: "#C4147D" },
  TM: { bg: "#FFF1AC", border: "#F5C518", text: "#8B6914" },
  VV: { bg: "#C8E6D5", border: "#006B31", text: "#004D23" },
  LT: { bg: "#C9DDFF", border: "#5B8DEF", text: "#2E5AAD" },
};

// Create brand options from Brands enum
const brandOptions = Object.entries(Brands).map(([key, value]) => ({
  value: value,
  label: BrandsFN[value as keyof typeof BrandsFN] || key,
}));

// Brand Selection Dialog Component - Responsive
const BrandSelectionDialog = ({
  isOpen,
  onClose,
  onSelect,
  currentBrand,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (brand: { value: string; label: string }) => void;
  currentBrand: string | null;
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Dialog - slides up from bottom on mobile, centered on larger screens */}
      <div className="relative z-10 w-full max-w-md p-4 mx-0 bg-white shadow-xl sm:p-6 rounded-t-2xl sm:rounded-xl sm:mx-4">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className="text-base font-semibold text-gray-800 sm:text-lg">Select Brand</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 transition-colors rounded-full hover:text-gray-600 hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Brand Grid - 3 columns on all sizes, but smaller items on mobile */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {brandOptions.map((brand) => {
            const colors = brandThemeColors[brand.value];
            const isSelected = currentBrand === brand.value;

            return (
              <button
                key={brand.value}
                onClick={() => {
                  onSelect(brand);
                  onClose();
                }}
                className={`flex flex-col items-center p-2 sm:p-3 rounded-lg transition-all ${
                  isSelected
                    ? "border-4"
                    : "border-2 border-transparent hover:scale-105 active:scale-95"
                }`}
                style={{
                  backgroundColor: `${colors.border}30`,
                  ...(isSelected && { borderColor: colors.border }),
                }}
              >
                <div className="flex items-center justify-center w-14 h-14 mb-1.5 bg-white rounded-full shadow sm:w-16 sm:h-16 sm:mb-2">
                  <img
                    src={brandLogos[brand.value]}
                    alt={brand.label}
                    className="object-contain w-10 h-10 sm:w-14 sm:h-14"
                  />
                </div>
                <span
                  className="text-[10px] sm:text-xs font-medium text-center leading-tight"
                  style={{ color: colors.text }}
                >
                  {brand.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Safe area padding for iPhone notch/home indicator */}
        <div className="h-2 sm:hidden" />
      </div>
    </div>
  );
};

// Custom hook for detecting screen size
const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState<"mobile" | "tablet" | "desktop">("desktop");

  useEffect(() => {
    const checkScreenSize = () => {
      if (window.innerWidth < 640) {
        setScreenSize("mobile");
      } else if (window.innerWidth < 1024) {
        setScreenSize("tablet");
      } else {
        setScreenSize("desktop");
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return screenSize;
};

function ProductKnowledge() {
  const [activeJob, setActiveJob] = useState<ProductLibProcessJob | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<{ value: string; label: string } | null>(
    brandOptions.find((opt) => opt.value === "FL") || null
  );
  const [knowledgeData, setKnowledgeData] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [viewType, setViewType] = useState<ViewType>("tree");
  const [isBrandDialogOpen, setIsBrandDialogOpen] = useState<boolean>(false);
  const screenSize = useScreenSize();

  const themeColors = brandThemeColors[selectedBrand?.value || "FL"] || brandThemeColors.FL;

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Fetch active job on component mount
  useEffect(() => {
    const fetchActiveJob = async () => {
      try {
        const jobs = await fetchActiveProductKnowledgeJobs();
        if (jobs && jobs.length > 0) {
          setActiveJob(jobs[0]);
        }
      } catch (err) {
        console.error("Error fetching active jobs:", err);
        setError("Failed to fetch active job");
      }
    };

    fetchActiveJob();
  }, []);

  // Fetch knowledge data when brand is selected and activeJob exists
  useEffect(() => {
    const fetchKnowledgeData = async () => {
      if (!activeJob?.id || !selectedBrand?.value) {
        setKnowledgeData([]);
        return;
      }

      setLoading(true);
      setError(null);
      setSearchTerm("");

      try {
        const data = await downloadProductKnowledgeData(activeJob.id, selectedBrand.value);
        setKnowledgeData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error downloading knowledge data:", err);
        setError("Failed to download knowledge data");
        setKnowledgeData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchKnowledgeData();
  }, [activeJob, selectedBrand]);

  // Filter products by search term and exclude Obsolete status
  const filteredProducts = useMemo(() => {
    // First, exclude Obsolete (06) products and products without valid category
    const nonObsoleteProducts = knowledgeData.filter(
      (p) => p.status !== "06" && p.category && p.category !== "-"
    );

    if (!searchTerm.trim()) return nonObsoleteProducts;

    const term = searchTerm.toLowerCase();
    return nonObsoleteProducts.filter(
      (p) =>
        p.name?.toLowerCase().includes(term) ||
        p.materialCode?.toLowerCase().includes(term) ||
        p.materialName?.toLowerCase().includes(term) ||
        p.barcode?.toLowerCase().includes(term) ||
        p.category?.toLowerCase().includes(term) ||
        p.subcategory?.toLowerCase().includes(term)
    );
  }, [knowledgeData, searchTerm]);

  return (
    <div className="container px-1 py-3 mx-auto sm:px-0 sm:py-6">
      <div className="mb-3 sm:mb-6">
        {/* Title, Active Job ID, and View Type Switch - responsive layout */}
        <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <h1 className="text-xl font-bold text-gray-800 sm:text-2xl">Product Knowledge</h1>

          {/* Controls - stack on mobile, row on larger screens */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            {/* View Type Switch and Export */}
            {!loading && knowledgeData.length > 0 && (
              <div className="flex items-center justify-between gap-2 sm:gap-3">
                {/* Export PDF Button */}
                <ExportPDFButton
                  products={filteredProducts}
                  themeColors={themeColors}
                  brandName={selectedBrand?.label || "Product Knowledge"}
                />

                {/* View Type Switch */}
                <div className="inline-flex p-1 bg-gray-100 rounded-lg">
                  <button
                    onClick={() => setViewType("tree")}
                    className={`flex items-center gap-1 px-2 py-1.5 text-xs sm:text-sm sm:gap-1.5 sm:px-3 font-medium rounded-md transition-all ${
                      viewType === "tree"
                        ? "bg-white shadow text-gray-900"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                      />
                    </svg>
                    <span className="hidden xs:inline">Tree</span>
                  </button>
                  <button
                    onClick={() => setViewType("folder")}
                    className={`flex items-center gap-1 px-2 py-1.5 text-xs sm:text-sm sm:gap-1.5 sm:px-3 font-medium rounded-md transition-all ${
                      viewType === "folder"
                        ? "bg-white shadow text-gray-900"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                      />
                    </svg>
                    <span className="hidden xs:inline">Folder</span>
                  </button>
                </div>
              </div>
            )}

            {/* Active Job ID - more compact on mobile */}
            {activeJob && (
              <div
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg sm:gap-2 sm:px-3 sm:py-2"
                style={{ backgroundColor: themeColors.bg }}
              >
                <span className="text-xs sm:text-sm" style={{ color: themeColors.text }}>
                  {screenSize === "mobile" ? "Job:" : "Active Job ID:"}
                </span>
                <span className="text-xs font-semibold sm:text-sm" style={{ color: themeColors.border }}>
                  {activeJob.id?.slice(0, screenSize === "mobile" ? 6 : 8)}
                </span>
                <button
                  onClick={() => activeJob.id && copyToClipboard(activeJob.id)}
                  className="p-1 transition-colors rounded hover:bg-white/50"
                  title={copied ? "Copied!" : "Copy full ID"}
                >
                  {copied ? (
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: themeColors.text }} viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                      <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                    </svg>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Brand Logo - clickable to open dialog - responsive sizing */}
        <div className="flex flex-col items-center my-4 sm:my-6">
          {selectedBrand && brandLogos[selectedBrand.value] && (
            <button
              onClick={() => setIsBrandDialogOpen(true)}
              disabled={!activeJob}
              className="relative group"
            >
              <div
                className="flex items-center justify-center w-16 h-16 overflow-hidden transition-transform bg-white border-2 rounded-full shadow-lg sm:w-24 sm:h-24 md:w-32 md:h-32 sm:border-4 group-hover:scale-105 group-disabled:opacity-50 group-disabled:cursor-not-allowed"
                style={{ borderColor: themeColors.border }}
              >
                <img
                  src={brandLogos[selectedBrand.value]}
                  alt={selectedBrand.label}
                  className="object-contain h-14 p-0.5 sm:h-20 sm:p-1 md:h-28"
                />
              </div>
              {/* Edit indicator */}
              {activeJob && (
                <div
                  className="absolute flex items-center justify-center w-5 h-5 transition-opacity bg-white border-2 rounded-full shadow sm:w-6 sm:h-6 -right-0.5 -top-0.5 sm:-right-1 sm:-top-1 group-hover:opacity-100 opacity-70"
                  style={{ borderColor: themeColors.border }}
                >
                  <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" style={{ color: themeColors.text }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </div>
              )}
              {loading ? (
                <div
                  className="absolute px-2 py-0.5 sm:px-3 sm:py-1 transform -translate-x-1/2 rounded-full -bottom-2 left-1/2"
                  style={{ backgroundColor: themeColors.border }}
                >
                  <div className="w-3 h-3 border-2 border-white rounded-full sm:w-4 sm:h-4 border-t-transparent animate-spin"></div>
                </div>
              ) : (
                filteredProducts.length > 0 && (
                  <div
                    className="absolute px-2 py-0.5 text-[10px] sm:px-3 sm:py-1 sm:text-xs font-bold text-center text-white transform -translate-x-1/2 rounded-full -bottom-2 left-1/2"
                    style={{ backgroundColor: themeColors.border }}
                  >
                    {filteredProducts.length.toLocaleString()}
                  </div>
                )
              )}
            </button>
          )}
          {/* Brand name label */}
          {selectedBrand && (
            <span
              className="mt-3 text-xs font-medium sm:mt-4 sm:text-sm"
              style={{ color: themeColors.text }}
            >
              {selectedBrand.label}
            </span>
          )}
        </div>

        {/* Brand Selection Dialog */}
        <BrandSelectionDialog
          isOpen={isBrandDialogOpen}
          onClose={() => setIsBrandDialogOpen(false)}
          onSelect={setSelectedBrand}
          currentBrand={selectedBrand?.value || null}
        />

        {/* Error/Warning States - responsive */}
        {!activeJob && (
          <div className="p-3 border border-yellow-200 rounded-lg sm:p-4 bg-yellow-50">
            <p className="text-sm text-yellow-700 sm:text-base">No active Product Knowledge job found.</p>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center min-h-[40vh] sm:min-h-[50vh]">
            <div
              className="w-10 h-10 mb-3 border-4 rounded-full sm:w-12 sm:h-12 sm:mb-4 border-t-transparent animate-spin"
              style={{ borderColor: themeColors.border, borderTopColor: "transparent" }}
            ></div>
            <span className="text-sm font-medium text-gray-600 sm:text-base">Loading products...</span>
          </div>
        )}

        {error && (
          <div className="p-3 border border-red-200 rounded-lg sm:p-4 bg-red-50">
            <p className="text-sm text-red-700 sm:text-base">{error}</p>
          </div>
        )}

        {/* Main Content - View Components */}
        {!loading && knowledgeData.length > 0 && (
          <>
            {viewType === "tree" ? (
              <TreeView
                products={filteredProducts}
                themeColors={themeColors}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            ) : (
              <FolderView
                products={filteredProducts}
                themeColors={themeColors}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ProductKnowledge;
